import React, { useState, useEffect } from 'react';
import { empleadosApi, tiendasApi } from '../services/api';

const Empleados = () => {
  const [empleados, setEmpleados] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmpleado, setEditingEmpleado] = useState(null);
  const [filtroActivo, setFiltroActivo] = useState('todos');
  const [filtroTienda, setFiltroTienda] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    usuario: '',
    correo: '',
    password: '',
    telefono: '',
    rol: 'tecnico',
    tienda_id: '',
    activo: true
  });

  useEffect(() => {
    loadData();
  }, [filtroActivo, filtroTienda]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroActivo !== 'todos') {
        params.activos = filtroActivo === 'activos' ? '1' : '0';
      }
      if (filtroTienda) {
        params.tienda_id = filtroTienda;
      }

      const [empRes, tiendasRes] = await Promise.all([
        empleadosApi.getAll(params),
        tiendasApi.getAll()
      ]);
      setEmpleados(empRes.data || []);
      setTiendas(tiendasRes.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (empleado = null) => {
    if (empleado) {
      setEditingEmpleado(empleado);
      setFormData({
        nombre: empleado.nombre,
        usuario: empleado.usuario || '',
        correo: empleado.correo || '',
        password: '', // No mostrar contraseña existente
        telefono: empleado.telefono || '',
        rol: empleado.rol || 'tecnico',
        tienda_id: empleado.tienda_id || '',
        activo: empleado.activo === 1 || empleado.activo === true
      });
    } else {
      setEditingEmpleado(null);
      setFormData({
        nombre: '',
        usuario: '',
        correo: '',
        password: '',
        telefono: '',
        rol: 'tecnico',
        tienda_id: '',
        activo: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEmpleado(null);
    setFormData({
      nombre: '',
      usuario: '',
      correo: '',
      password: '',
      telefono: '',
      rol: 'tecnico',
      tienda_id: '',
      activo: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      // Si no se cambió la contraseña, no enviarla
      if (!dataToSend.password) {
        delete dataToSend.password;
      }
      // Convertir tienda_id vacío a null
      if (dataToSend.tienda_id === '') {
        dataToSend.tienda_id = null;
      }

      if (editingEmpleado) {
        await empleadosApi.update(editingEmpleado.id, dataToSend);
      } else {
        await empleadosApi.create(dataToSend);
      }
      loadData();
      handleCloseModal();
    } catch (error) {
      alert(error.response?.data?.error || 'Error al guardar el empleado');
    }
  };

  const handleToggleActive = async (empleado) => {
    try {
      await empleadosApi.update(empleado.id, {
        activo: !empleado.activo
      });
      loadData();
    } catch (error) {
      alert('Error al actualizar el empleado');
    }
  };

  const handleDelete = async (empleado) => {
    if (!window.confirm(`¿Está seguro de eliminar a ${empleado.nombre}?`)) {
      return;
    }
    try {
      await empleadosApi.delete(empleado.id);
      loadData();
    } catch (error) {
      alert('Error al eliminar el empleado');
    }
  };

  const getRolLabel = (rol) => {
    const labels = {
      admin: 'Administrador',
      tecnico: 'Técnico',
      recepcionista: 'Recepcionista'
    };
    return labels[rol] || rol;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Empleados</h1>
          <p className="page-subtitle">{empleados.filter(e => e.activo).length} empleados activos</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Nuevo Empleado
        </button>
      </div>

      {/* Filtros */}
      <div className="filters-bar">
        <div className="form-group" style={{ marginBottom: 0, minWidth: '150px' }}>
          <select
            className="form-control"
            value={filtroActivo}
            onChange={(e) => setFiltroActivo(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
          <select
            className="form-control"
            value={filtroTienda}
            onChange={(e) => setFiltroTienda(e.target.value)}
          >
            <option value="">Todas las sucursales</option>
            {tiendas.map(t => (
              <option key={t.id} value={t.id}>{t.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : empleados.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <h3>No hay empleados</h3>
            <p>Registra tu primer empleado</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Usuario</th>
                  <th>Rol</th>
                  <th>Sucursal</th>
                  <th>Teléfono</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empleados.map(empleado => (
                  <tr key={empleado.id} style={{ opacity: empleado.activo ? 1 : 0.5 }}>
                    <td style={{ fontWeight: 500 }}>{empleado.nombre}</td>
                    <td>{empleado.usuario || '-'}</td>
                    <td>
                      <span className={`role-badge role-${empleado.rol}`}>
                        {getRolLabel(empleado.rol)}
                      </span>
                    </td>
                    <td>{empleado.tienda_nombre || 'Sin asignar'}</td>
                    <td>{empleado.telefono || '-'}</td>
                    <td>
                      <span className={`status-badge ${empleado.activo ? 'status-terminada' : 'status-pendiente'}`}>
                        {empleado.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          onClick={() => handleOpenModal(empleado)}
                          className="btn btn-secondary btn-icon"
                          title="Editar"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleToggleActive(empleado)}
                          className={`btn btn-icon ${empleado.activo ? 'btn-danger' : 'btn-success'}`}
                          title={empleado.activo ? 'Desactivar' : 'Activar'}
                        >
                          {empleado.activo ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="15" y1="9" x2="9" y2="15"/>
                              <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(empleado)}
                          className="btn btn-icon btn-danger"
                          title="Eliminar"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingEmpleado ? 'Editar Empleado' : 'Nuevo Empleado'}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nombre Completo *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Usuario (para login)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.usuario}
                      onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                      placeholder="Ej: juan.perez"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      {editingEmpleado ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={editingEmpleado ? 'Dejar vacío para no cambiar' : ''}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Correo Electrónico</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.correo}
                      onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rol *</label>
                    <select
                      className="form-control"
                      value={formData.rol}
                      onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                      required
                    >
                      <option value="tecnico">Técnico</option>
                      <option value="recepcionista">Recepcionista</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Sucursal Asignada</label>
                    <select
                      className="form-control"
                      value={formData.tienda_id}
                      onChange={(e) => setFormData({ ...formData, tienda_id: e.target.value })}
                    >
                      <option value="">Sin asignar (todas)</option>
                      {tiendas.filter(t => t.activa).map(t => (
                        <option key={t.id} value={t.id}>{t.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={formData.activo}
                        onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      />
                      Empleado Activo
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Empleados;
